/**
 * Turn a redemption HandoffTarget into a destination URL. The campaign app is a
 * thin orchestrator — it navigates the browser to the owning app (SSO cookie
 * carries the session, ADR 0038).
 */
type Handoff =
  | { app: 'grow'; kind: 'product'; productId: string; moduleId?: string; contentId?: string }
  | { app: 'grow'; kind: 'group'; groupId: string }
  | { app: 'grow'; kind: 'community'; communityId: string }
  | { app: 'store'; kind: 'product'; productId: string; storeOfferId: string }

const growBase = () => import.meta.env.VITE_GROW_URL || 'https://app.polux.weapps.net'
/** ADR 0161 §4 (amended): the browser home of the free campaign's preview + sign-in — a host no native shell
 *  claims, so the campaign never opens the app before the product is granted. Falls back to app.polux. */
const growWebBase = () => import.meta.env.VITE_GROW_WEB_URL || growBase()
const storeBase = () => import.meta.env.VITE_STORE_URL || 'https://store.polux.weapps.net'

export function handoffUrl(handoff: Handoff, tenantSlug: string, opts: { campaignCode?: string; acct?: string | null } = {}): string {
  // grow-mobile resolves products/groups/entitlements under the Active Runtime
  // Tenant Context (x-tenant-slug). A deep link MUST carry ?tenant=<slug> so it
  // opens under the campaign's tenant — otherwise grow keeps the user's prior
  // tenant and the product/entitlement lookup 403s (ADR 0056/0078/0079).
  // "The link opens in the browser; the product opens in the app": a granted product is ALWAYS handed to
  // app.polux — the installed app when there is one (Android App Link), the web app otherwise.
  const t = encodeURIComponent(tenantSlug)
  const grow = growBase()
  switch (handoff.kind) {
    case 'product': {
      if (handoff.app === 'store') {
        // A sale hand-off IS the Upgrade Link (buy intent on the paid offer); the campaign travels on it
        // (Campaign Attribution, ADR 0161 §5).
        const c = opts.campaignCode ? `&c=${encodeURIComponent(opts.campaignCode)}` : ''
        return `${storeBase()}/store/${t}?buy=${encodeURIComponent(handoff.storeOfferId)}${c}`
      }
      const p = encodeURIComponent(handoff.productId)
      // Deep-link straight to the targeted content page when the campaign designated one (both ids present);
      // otherwise fall back to the product overview. The content route renders the reader without a group.
      // `fresh=1`: a campaign arrival means access may just have changed (a claim, or a purchase made on
      // another surface) — the app must not trust its persisted module padlocks or bootstrap (ADR 0159 §3).
      // `acct`: the acquiring account's opaque id, for the Account Mismatch Notice (ADR 0162 §3).
      const acct = opts.acct ? `&acct=${encodeURIComponent(opts.acct)}` : ''
      if (handoff.moduleId && handoff.contentId) {
        return `${grow}/produtos/produto/${p}/modulo/${encodeURIComponent(handoff.moduleId)}/conteudo/${encodeURIComponent(handoff.contentId)}?tenant=${t}&fresh=1${acct}`
      }
      return `${grow}/produtos/produto/${p}?tenant=${t}&fresh=1${acct}`
    }
    case 'group':
      return `${grow}/comunidade/grupo/${encodeURIComponent(handoff.groupId)}?tenant=${t}`
    case 'community':
      return `${grow}/comunidade?community=${encodeURIComponent(handoff.communityId)}&tenant=${t}`
  }
}

/** ADR 0161 §3: a Free Product Campaign lands on its preview page — the Grow App's campaign route, in the
 *  BROWSER (web-only host): anonymous → preview, signed in → grant, then the product is handed to app.polux. */
export function freeCampaignEntryUrl(code: string, visitorId: string): string {
  return `${growWebBase()}/c/${encodeURIComponent(code)}?v=${encodeURIComponent(visitorId)}`
}

/** The Upgrade Link with buy intent, carrying the campaign (ADR 0159 §2, ADR 0161 §5). */
export function upgradeLinkUrl(tenantSlug: string, offerId: string, campaignCode: string): string {
  return `${storeBase()}/store/${encodeURIComponent(tenantSlug)}?buy=${encodeURIComponent(offerId)}&c=${encodeURIComponent(campaignCode)}`
}

/**
 * Whether the hand-off must be a TAP rather than a redirect (ADR 0173 §4).
 *
 * "The product opens in the app" (ADR 0161) only happens if the OS hands the link to the installed app, and
 * neither iOS nor Android does that for a scripted navigation — only for a link the person taps. The old
 * automatic redirect after the grant therefore always stayed in the browser. On a phone, the granted
 * product is offered as a tap; on a desktop there is no app to open, so the redirect stays. A sale (the
 * store) is a web surface either way.
 */
export function mustTapToOpenApp(handoff: Handoff, userAgent: string): boolean {
  if (handoff.app !== 'grow') return false
  return /iPhone|iPad|iPod|Android/i.test(userAgent)
}

/**
 * ADR 0177 §2: on a phone, a granted product ends on the Continue-in-App Screen — names the account, opens the app,
 * gets it, or goes on in the browser with the products. It lives on the web app's unclaimed host, so the screen itself
 * never opens the app; its buttons do, on the person's tap.
 */
export function continueInAppUrl(growHref: string): string {
  const url = new URL(growHref)
  const base = import.meta.env.VITE_GROW_WEB_URL || 'https://web.polux.weapps.net'
  return `${base}/abrir?to=${encodeURIComponent(url.pathname + url.search)}&kind=campaign`
}
